import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Heart, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Download } from "lucide-react";

type HealthLevel = "green" | "orange" | "red";

function getHealthLevel(days: number | null, totalActivity: number): HealthLevel {
  if (days === null) return "red"; // never logged in
  if (days >= 60) return "red";
  if (days >= 30 || totalActivity === 0) return "orange";
  return "green";
}

function HealthBadge({ level }: { level: HealthLevel }) {
  if (level === "green")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success">
        <CheckCircle2 className="h-3 w-3" />
        Actif
      </span>
    );
  if (level === "orange")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-warning/10 text-warning">
        <AlertTriangle className="h-3 w-3" />
        À surveiller
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-destructive/10 text-destructive">
      <XCircle className="h-3 w-3" />
      À risque
    </span>
  );
}

function HealthScore({ level }: { level: HealthLevel }) {
  const score = level === "green" ? 85 : level === "orange" ? 50 : 20;
  const color =
    level === "green" ? "text-success" :
    level === "orange" ? "text-warning" :
    "text-destructive";
  return <span className={`font-bold ${color}`}>{score}</span>;
}

function downloadCSV(data: any[]) {
  const headers = ["Nom", "Plan", "Score", "Statut", "Jours inactif", "Devis", "Factures", "Interventions"];
  const rows = data.map((t) => {
    const level = getHealthLevel(t.daysSinceLogin, t.totalQuotes + t.totalInvoices + t.totalInterventions);
    const score = level === "green" ? 85 : level === "orange" ? 50 : 20;
    return [
      t.name,
      t.plan ?? "",
      score,
      level === "green" ? "Actif" : level === "orange" ? "À surveiller" : "À risque",
      t.daysSinceLogin ?? "jamais",
      t.totalQuotes,
      t.totalInvoices,
      t.totalInterventions,
    ].join(";");
  });
  const csv = [headers.join(";"), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tenant-health-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TenantHealth() {
  const { data: tenants, isLoading, refetch } = trpc.admin.analytics.churnRisk.useQuery();

  const withHealth = (tenants ?? []).map((t) => ({
    ...t,
    healthLevel: getHealthLevel(t.daysSinceLogin, t.totalQuotes + t.totalInvoices + t.totalInterventions),
  }));

  const sorted = [...withHealth].sort((a, b) => {
    const order = { red: 0, orange: 1, green: 2 };
    return order[a.healthLevel] - order[b.healthLevel];
  });

  const redCount = sorted.filter((t) => t.healthLevel === "red").length;
  const orangeCount = sorted.filter((t) => t.healthLevel === "orange").length;
  const greenCount = sorted.filter((t) => t.healthLevel === "green").length;

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
              <Heart className="h-7 w-7" />
              Health & Churn Risk
            </h1>
            <p className="text-muted-foreground">Score de santé par tenant — intervenez avant le churn</p>
          </div>
          <div className="flex gap-2">
            {!isLoading && tenants && (
              <Button variant="outline" size="sm" onClick={() => downloadCSV(tenants)}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-success/20">
            <CardContent className="flex items-center gap-4 p-5">
              <CheckCircle2 className="h-8 w-8 text-success" />
              <div>
                <div className="text-2xl font-bold text-success">
                  {isLoading ? "—" : greenCount}
                </div>
                <p className="text-sm text-muted-foreground">Tenants actifs (score ≥ 70)</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-warning/20">
            <CardContent className="flex items-center gap-4 p-5">
              <AlertTriangle className="h-8 w-8 text-warning" />
              <div>
                <div className="text-2xl font-bold text-warning">
                  {isLoading ? "—" : orangeCount}
                </div>
                <p className="text-sm text-muted-foreground">À surveiller (score 40–69)</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-destructive/20">
            <CardContent className="flex items-center gap-4 p-5">
              <XCircle className="h-8 w-8 text-destructive" />
              <div>
                <div className="text-2xl font-bold text-destructive">
                  {isLoading ? "—" : redCount}
                </div>
                <p className="text-sm text-muted-foreground">À risque — action requise</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tenant Health Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tableau de santé des tenants</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-2">
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : sorted.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success" />
                Tous les tenants sont actifs
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-3 font-medium">Tenant</th>
                      <th className="text-center px-4 py-3 font-medium">Score</th>
                      <th className="text-center px-4 py-3 font-medium">Statut</th>
                      <th className="text-right px-4 py-3 font-medium">Inactivité</th>
                      <th className="text-right px-4 py-3 font-medium">Devis</th>
                      <th className="text-right px-4 py-3 font-medium">Factures</th>
                      <th className="text-right px-4 py-3 font-medium">Interventions</th>
                      <th className="text-center px-4 py-3 font-medium">Plan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sorted.map((t) => (
                      <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium">{t.name}</td>
                        <td className="px-4 py-3 text-center">
                          <HealthScore level={t.healthLevel} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <HealthBadge level={t.healthLevel} />
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground">
                          {t.daysSinceLogin !== null ? `${t.daysSinceLogin}j` : "jamais"}
                        </td>
                        <td className="px-4 py-3 text-right">{t.totalQuotes}</td>
                        <td className="px-4 py-3 text-right">{t.totalInvoices}</td>
                        <td className="px-4 py-3 text-right">{t.totalInterventions}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={t.plan ? "secondary" : "outline"}>{t.plan ?? "—"}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
